import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as BlueStart } from "assets/images/svgs/attendee/blue-starr.svg";
import { ReactComponent as EditSvg } from "assets/images/svgs/attendee/edit-2.svg";
import { ReactComponent as SettingSvg } from "assets/images/svgs/attendee/setting-4.svg";
import { ReactComponent as Award } from "assets/images/svgs/ambassador/award.svg";
import { ReactComponent as AwardLink } from "assets/images/svgs/award-link.svg";
import { ReactComponent as Verify } from "assets/images/svgs/ambassador/verify.svg";
import { ReactComponent as Support } from "assets/images/svgs/ambassador/support.svg";
import { ReactComponent as Logout } from "assets/images/svgs/ambassador/logout.svg";
import { ReactComponent as FolderOpen } from "assets/images/svgs/ambassador/folderopen.svg";
import { ReactComponent as Medal } from "assets/images/svgs/ambassador/medal.svg";
import CustomerSupport from "../CustomerSupport";
import useStore from "hooks/useStore";
import { siteRoutes } from "utils/helpers/enums/routes";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { 
  MdCalendarToday as CalendarTick,
  MdClose as CrossRed,
} from "react-icons/md";
import LearnLocalLogo from "assets/images/others/host-side-images/LL-Full Color-4.png";
import { differenceInDays, differenceInMonths, differenceInYears, format, isAfter } from "date-fns";
import { useSelector } from "react-redux";
import { CommunityIconAmbassador, WalletIcon } from "assets/images/svgs";

// Profile level enum matching Flutter code
enum ProfileLevel {
  approved = 'approved',
  sessionOnGoing = 'sessionOnGoing',
  waitingForApproval = 'waitingForApproval',
  profileCompleted = 'profileCompleted',
  rejected = 'rejected',
  profileUnCompleted = 'profileUnCompleted',
}

interface ProfileLevelConfig {
  title: string;
  message: string;
  color: string;
  imagePath: string | React.ReactNode;
  showButton: boolean;
  buttonText?: string;
  buttonAction?: (e: React.MouseEvent) => void;
}

interface ProfileLevelModalProps {
  onClose: () => void;
  onContinue: (e: React.MouseEvent) => void;
  registeredData: any;
}

// Profile Level Modal Component
const ProfileLevelModal: React.FC<ProfileLevelModalProps> = ({ onClose, onContinue, registeredData }) => {
  // Check profile level - matching Flutter logic exactly
  const checkProfileLevel = (): ProfileLevel => {
    const ambassador = registeredData?.profiles?.ambassador;

    if (ambassador == null || !ambassador.profileCompleted) {
      return ProfileLevel.profileUnCompleted;
    }

    if (ambassador.isApproved == null) {
      if (ambassador.bookedSessions != null && 
          ambassador.bookedSessions.startTime) {
        const sessionStartTime = new Date(ambassador.bookedSessions.startTime);
        if (isAfter(sessionStartTime, new Date())) {
          return ProfileLevel.sessionOnGoing;
        }
      }
      return ProfileLevel.waitingForApproval;
    }

    return ambassador.isApproved
      ? ProfileLevel.approved
      : ProfileLevel.rejected;
  };

  // Get configuration based on profile level
  const getProfileLevelConfig = (profileLevel: ProfileLevel): ProfileLevelConfig => {
    switch (profileLevel) {
      case ProfileLevel.profileUnCompleted:
        return {
          title: "Profile Incomplete",
          message: "Please complete your profile to continue",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: true,
          buttonText: "Continue",
          buttonAction: onContinue,
        };
      
      case ProfileLevel.waitingForApproval:
        return {
          title: "Thank you for Attending Live Session",
          message: "You have successfully taken Live Session for Ambassador Profile. Please wait for confirmation from Admin to unlock further features",
          color: "text-primaryBlueText",
          imagePath: <CalendarTick className="w-32 h-32 text-primaryBlueText mx-auto" />,
          showButton: false,
        };
      
      case ProfileLevel.sessionOnGoing:
        return {
          title: "Take a Live Session First!",
          message: "You currently have an active session",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: false,
        };
      
      case ProfileLevel.rejected:
        return {
          title: "Profile Rejected",
          message: "Your profile was not approved. Please contact support.",
          color: "text-red-500",
          imagePath: <CrossRed className="w-32 h-32 text-red-500 mx-auto" />,
          showButton: false,
        };
      
      case ProfileLevel.profileCompleted:
        return {
          title: "Profile Complete",
          message: "Your profile is complete and ready for submission",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: false,
        };
      
      case ProfileLevel.approved:
        // Should not show modal for approved, but handle it just in case
        return {
          title: "",
          message: "",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: false,
        };
      
      default:
        return {
          title: "Profile Incomplete",
          message: "Please complete your profile to continue",
          color: "text-primaryBlueText",
          imagePath: LearnLocalLogo,
          showButton: true,
          buttonText: "Continue",
          buttonAction: onContinue,
        };
    }
  };

  const profileLevel = useMemo(() => checkProfileLevel(), [registeredData]);
  const config = useMemo(() => getProfileLevelConfig(profileLevel), [profileLevel]);

  // Don't show modal if approved (shouldn't happen, but safety check)
  if (profileLevel === ProfileLevel.approved) {
    return null;
  }

  // Render image based on type
  const renderImage = () => {
    if (typeof config.imagePath === 'string') {
      return (
        <img
          src={config.imagePath}
          alt={config.title}
          className="w-32 mx-auto mb-2"
        />
      );
    }
    return config.imagePath;
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" 
      style={{ zIndex: 'calc(var(--z-drawer) + 1)' }}
      onClick={(e) => {
        // Only close if clicking on the backdrop, not the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white p-6 rounded-lg shadow-lg w-[85%] sm:w-[364px] text-center transform transition-transform duration-300 ease-in-out translate-x-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="text-secondaryText text-3xl rounded"
          >
            <IoIosCloseCircleOutline />
          </button>
        </div>
        <div className="flex justify-center">
          {renderImage()}
        </div>
        <h2 className={`text-base ${config.color} font-semibold mb-4`}>
          {config.title}
        </h2>
        <p className="text-labelTextColor text-sm mb-4">
          {config.message}
        </p>
        {config.showButton && config.buttonText && config.buttonAction && (
          <button
            onClick={config.buttonAction}
            className="bg-primaryBlueText text-white px-4 py-2 rounded-full"
          >
            {config.buttonText}
          </button>
        )}
      </div>
    </div>
  );
};

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const AmbassadorNavbarDrawer: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [showSupportDrawer, setShowSupportDrawer] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);
  const [profileCompleted, setProfileCompleted] = useState<boolean | null>(
    null
  );
  
  const { getUserProfilePic, getUserFullName ,getAmbassadorprofileType,getAmbassadorCreatedAt,getAmbassadorLevel } = useStore();
  const userProfilePic = getUserProfilePic();
  const userFullName = getUserFullName();
  const ambassadorprofileType = getAmbassadorprofileType();
  const ambassadorCreatedAt = getAmbassadorCreatedAt();
  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );
  const ambassadorTier = getAmbassadorLevel();
  
  useEffect(() => {
    if (registeredData) {
      const completed = registeredData?.profiles?.ambassador?.profileCompleted;
      setProfileCompleted(completed);
    }
  }, [registeredData]);

  // Check profile level - matching Flutter logic exactly
  const checkProfileLevel = (): ProfileLevel => {
    const ambassador = registeredData?.profiles?.ambassador;

    if (ambassador == null || !ambassador.profileCompleted) {
      return ProfileLevel.profileUnCompleted;
    }

    if (ambassador.isApproved == null) {
      if (ambassador.bookedSessions != null && 
          ambassador.bookedSessions.startTime) {
        const sessionStartTime = new Date(ambassador.bookedSessions.startTime);
        if (isAfter(sessionStartTime, new Date())) {
          return ProfileLevel.sessionOnGoing;
        }
      }
      return ProfileLevel.waitingForApproval;
    }

    return ambassador.isApproved
      ? ProfileLevel.approved
      : ProfileLevel.rejected;
  };

  // Check if step one (user profile) is complete
  const isStepOneComplete = () => {
    const user = registeredData?.user;
    return !!(
      user?.firstName &&
      user?.lastName &&
      user?.email &&
      user?.language &&
      user?.address &&
      user?.phone &&
      user?.dob &&
      user?.gender &&
      user?.state &&
      user?.city &&
      user?.zipCode
    );
  };

  const handleCompleteProfileClick = () => {
    // If step one is complete, navigate to welcome page
    // Otherwise, navigate to profile page to complete step one
    if (isStepOneComplete()) {
      navigate(siteRoutes.ambassadorWelcome);
    } else {
      navigate(siteRoutes.ambassadorProfile);
    }
    onClose();
  };

  const handleNavigation = (item: any) => {
    if (item.label === "Logout") {
      localStorage.clear();
      onClose();
      navigate(item.link);
      return;
    }
    if (item.label === "Edit Profile") {
      if (profileCompleted === false || profileCompleted === null) {
        onClose();
        navigate(siteRoutes.ambassadorProfile);
        return;
      } else {
        onClose();
        navigate(siteRoutes.ambassadorEditProfile);
        return;
      }
    }

    if (
      item.label === "General Settings" ||
      item.label === "Community" ||
      item.label === "Resource Library" ||
      item.label === "Experiences" ||
      item.label === "My Badges" ||
      item.label === "Wallet" 
    ) {
      const profileLevel = checkProfileLevel();
      // Show modal for all profile levels except approved (matching Flutter behavior)
      if (profileLevel !== ProfileLevel.approved) {
        setShowCompleteProfileModal(true);
      } else {
        onClose();
        navigate(item.link);
      }
      return;
    }

    if (item.label === "Customer Support") {
      // Open the Customer Support modal
      setShowSupportDrawer(true);

      return;
    }
    onClose();
    navigate(item.link);
  };

  const now = new Date();
    const createdAt = new Date(ambassadorCreatedAt);
    const years = differenceInYears(now, createdAt);
    const months = differenceInMonths(now, createdAt);
    const days = differenceInDays(now, createdAt);
    let timeSince = '';
    if (years >= 1) {
      timeSince = `${years} year${years > 1 ? 's' : ''}`;
    } else if (months >= 1) {
      timeSince = `${months} month${months > 1 ? 's' : ''}`;
    } else {
      timeSince = `${days} day${days > 1 ? 's' : ''}`;
    }

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{ zIndex: 'calc(var(--z-drawer) + 1)' }}
        onClick={onClose}
    >
      <div
       className={`fixed top-0 right-0 h-full w-4/5 sm:w-1/2 md:w-1/3 lg:w-[480px] overflow-y-auto bg-white p-4 md:p-6 lg:p-10 shadow-lg transition-transform duration-300 ease-in-out transform ${isOpen ? "translate-x-0" : "translate-x-full"
       }`}
     onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl text-gray-600 hover:text-black"
        >
          &times;
        </button>

        <div className="flex items-center space-x-4 mt-6">
          <img
            src={userProfilePic}
            alt="Profile"
            className="w-20 h-20 rounded-full object-cover"
          />
          <div>
            <h2 className="text-lg font-semibold text-primaryBlueText capitalize">
              {userFullName}
            </h2>
            <span className="flex items-center capitalize justify-center text-sm w-fit px-3 h-[34px] rounded-full bg-[#F0F2F3] text-primaryBlueText">
              <BlueStart className="mr-1" /> {timeSince} Member
            </span>
          </div>
        </div>

        {/* Stats Section */}
        <div className="flex items-center gap-2 sm:gap-2  flex-wrap lg:flex-nowrap bg-attendeBlueBg rounded-lg lg:rounded-full px-3 py-3 mt-6">
          <span className="flex items-center text-base whitespace-nowrap text-primaryBlueText">
            <Award className="mr-1" />
             {ambassadorTier === "tierOne" && (
                <span >Community Builder</span>
              )}
              {ambassadorTier === "tierTwo" && (
                <span >Community Advocate</span>
              )}
              {ambassadorTier === "tierThree" && (
                <span>Community Leader</span>
              )}
          </span>
          <span className="flex items-center text-base whitespace-nowrap text-primaryBlueText">
            <Medal className="mr-1" />
            {timeSince}
          </span>
        </div>

        <div className="mt-3 flex flex-col ">
          {/* Only visible on screens smaller than md */}
          <div className="md:hidden flex flex-col">
            {[
              {
                label: "Community",
                icon: <CommunityIconAmbassador />,
                link: "/ambassador/communities",
              },
              {
                label: "Wallet",
                icon: <WalletIcon />,
                link: "/ambassador/wallet",
              },
            ].map((item, index) => (
              <div
                key={index}
                onClick={() => handleNavigation(item)}
                className="flex items-center py-5 w-full text-left text-darkBlueText bordr-linear cursor-pointer"
              >
                <span className="mr-3 text-lg">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </div>

          {/* Other links (visible on all screen sizes) */}
          <div className="flex flex-col">
            {[
              {
                label: "Edit Profile",
                icon: <EditSvg />,
                link: "",
              },

              {
                label: "Resource Library",
                icon: <FolderOpen />,
                link: "/ambassador/resource-library",
              },
              { label: "My Badges", icon: <Verify />, link: "/ambassador/my-badges" },

              {
                label: "General Settings",
                icon: <SettingSvg />,
                link: "/ambassador/general-settings",
              },
              {
                label: "Customer Support",
                icon: (
                  // You can replace this with a more appropriate icon if needed.
                  <Support />
                ),
                link: "#",
              },
              { label: "Logout", icon: <Logout />, link: "/auth/login" },
            ].map((item, index, array) => (
              <div
              key={index}
              onClick={() => handleNavigation(item)}
              className={`flex  items-center w-full text-left text-darkBlueText cursor-pointer ${
                index === array.length - 1 ? " pt-5" : "bordr-linear py-5"
              }`}
              >
                <span className="mr-3 text-lg">{item.icon}</span>
                {item.label}
              </div>  
            ))}
          </div>
        </div>
      </div>
      {/* Render modals outside the drawer overlay */}
      {showCompleteProfileModal && <ProfileLevelModal 
        onClose={() => setShowCompleteProfileModal(false)}
        onContinue={handleCompleteProfileClick}
        registeredData={registeredData}
      />}
      {showSupportDrawer && (
        <CustomerSupport
          isOpen={showSupportDrawer}
          onClose={() => setShowSupportDrawer(false)}
        />
      )}
    </div>
  );
};

export default AmbassadorNavbarDrawer;
